import {
  Component,
  DestroyRef,
  ViewEncapsulation,
  afterNextRender,
  computed,
  inject,
  input,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { HubSpotRegion, loadHubSpotScript } from './hubspot-form-loader';

// ---------------------------------------------------------------------------
// HubSpot form embed block.
//
// Mount strategy — we use `afterNextRender` (NOT `isPlatformBrowser` +
// `setTimeout`). Justification:
//   * `afterNextRender` is Angular 18+'s purpose-built browser-only hook.
//   * It fires AFTER the target `<div>` is in the DOM (so `target: '#id'`
//     resolves).
//   * It runs exactly once per instance and never executes on the server.
//   * It avoids the macrotask race that `setTimeout(0)` introduces.
//
// Destroy safety — we hold a plain closure flag flipped by `DestroyRef` so a
// late-arriving script load cannot call `hbspt.forms.create` against a
// detached DOM node. We do NOT use AbortController because the `<script>`
// tag itself cannot be aborted once appended — what we must avoid is
// mounting the form into a target that no longer exists.
// ---------------------------------------------------------------------------

/**
 * Derive an aria-label string for a HubSpot-injected <select>.
 * Priority: closest preceding <label> text → name attribute (title-cased) → "Select an option".
 * HubSpot v2 embed structure (stable):
 *   <div class="hs-form-field">
 *     <label class="hs-main-font-element"><span>Country</span></label>
 *     <div class="input"><select name="country">...</select></div>
 *   </div>
 */
function inferLabelFor(sel: HTMLSelectElement): string {
  const field = sel.closest('.hs-form-field');
  const labelEl = field?.querySelector('label');
  const labelText = labelEl?.textContent?.trim();
  if (labelText) return labelText;
  const name = sel.getAttribute('name');
  if (name) return name.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return 'Select an option';
}

@Component({
  selector: 'ds-hubspot-form-block',
  templateUrl: './hubspot-form-block.html',
  styleUrl: './hubspot-form-block.scss',
  encapsulation: ViewEncapsulation.None,
})
export class HubSpotFormBlockComponent {
  readonly portalId = input.required<string>();
  readonly formId = input.required<string>();
  readonly region = input<HubSpotRegion>('eu1');
  readonly heading = input<string>('');
  readonly intro = input<string>('');
  readonly blockId = input.required<number>();

  readonly targetId = computed(() => `hs-form-${this.blockId()}`);

  constructor() {
    const doc = inject(DOCUMENT);
    const destroyed = { value: false };
    inject(DestroyRef).onDestroy(() => {
      destroyed.value = true;
    });

    // A11Y-03 — capture DestroyRef for the MutationObserver cleanup wired
    // INSIDE the afterNextRender callback below. Must be captured here in
    // the constructor body (injection context still open) — calling
    // `inject(DestroyRef)` from inside the afterNextRender callback would
    // throw NG0203 (the injection context is closed by the time the
    // callback fires). Mirrors the pattern at lines 50-53 above (the
    // `destroyed.value` flag) and `const doc = inject(DOCUMENT)` at line 49.
    // Angular permits multiple `.onDestroy(...)` registrations on a single
    // DestroyRef, so this does NOT replace the destroyed-flag cleanup —
    // they coexist on the same DestroyRef.
    const destroyRef = inject(DestroyRef);

    afterNextRender(async () => {
      try {
        await loadHubSpotScript(doc, this.region());
        if (destroyed.value) return;
        const w = window as unknown as {
          hbspt?: { forms: { create: (opts: unknown) => void } };
        };
        if (!w.hbspt) return;
        w.hbspt.forms.create({
          portalId: this.portalId(),
          formId: this.formId(),
          region: this.region(),
          target: `#${this.targetId()}`,
        });
      } catch {
        // Loader rejected (network error). Leave the wrapper visible; do not
        // throw — a thrown error here would break the whole dynamic zone.
      }

      // A11Y-03 — backfill aria-label on HubSpot-injected <select> elements.
      // Three mandatory guards (Pitfalls 12 + 13):
      //   (a) selector `select:not([aria-label])` — idempotency: a labeled
      //       select is skipped on subsequent observer fires.
      //   (b) observer config `{ childList: true, subtree: true }` ONLY —
      //       no `attributes` key, so setAttribute('aria-label', ...) does
      //       NOT trigger the observer's own callback (CPU loop prevention).
      //   (c) `destroyRef.onDestroy(() => observer.disconnect())` —
      //       SPA-nav cleanup; observer is per-component, not per-loader.
      //       `destroyRef` is captured in the constructor body above; a
      //       fresh DI lookup HERE would throw NG0203 (injection context is
      //       closed once the afterNextRender callback fires).
      const target = doc.getElementById(this.targetId());
      if (!target) return;

      const labelSelects = (): void => {
        const unlabeled = target.querySelectorAll<HTMLSelectElement>('select:not([aria-label])');
        for (const sel of unlabeled) {
          const labelText = inferLabelFor(sel);
          if (labelText) sel.setAttribute('aria-label', labelText);
        }
      };

      const observer = new MutationObserver(labelSelects);
      observer.observe(target, { childList: true, subtree: true });
      destroyRef.onDestroy(() => observer.disconnect());

      // Initial sweep — first paint may have rendered a <select> already
      // (HubSpot can mount synchronously when the script is cached).
      labelSelects();
    });
  }
}
